/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime} from './AdobeMetricsRuntime';
import type Configuration from './Configuration';
import {
  DigitalData,
  EventData,
  Level,
  Metric,
  MetricsApi,
  MetricsEvents,
  MetricsState,
  PageData,
  RecordType,
  Timer,
  User,
  UserData,
} from '@exc/metrics';
import {METRICS_VERSION} from './BuildVersion';
import uuidv1 from 'uuid/v1';

export interface MetricsProcessing {
  applyPageState(state?: MetricsState): MetricsState;
  create(recordType: RecordType, name?: string, ...args: any): Metric;
  log(
    recordType: RecordType,
    level: Level,
    name?: string,
    message?: string,
    event?: string | string[],
    ...args: any
  ): void;
  processQueue(flush?: string): Promise<number>;
  updateFlushEvent(config: Configuration): void;
}

// Flush period before pageDone, it is reduced thereafter in isFlushEventFn
export const INIT_FLUSH_PERIOD = 15000;
const ANONYMOUS = 'anonymous';
const re = /adobe_(|dx_)optout=/gi;

/**
 * Metric that has been set via setUser().
 *
 * @param {User} user The user properties to transmogrify.
 * @returns {Metric} A relevant Metric to send to Gateway.
 * @private
 */
function _createSetUserMetric(user: User): Metric {
  return {
    event: 'exc.metrics.setUser',
    level: Level.INFO,
    recordType: RecordType.EVENT,
    user,
  };
}

/**
 * Metric that has been cleared via clearUser().
 *
 * @returns {Metric} A minimal Metric.
 * @private
 */
function _clearMetric(): Metric {
  return {
    event: 'exc.metrics.clearUser',
    level: Level.INFO,
    recordType: RecordType.EVENT,
    user: {},
  };
}

/**
 * Template for an OMEGA UserData payload.  When OMEGA is supported by Gateway, this
 * method can be implemented on the server.
 *
 * @param {User} user The user properties to transmogrify.
 * @param {boolean} isSet True if called by setUser, false if called by clearUser.
 * @returns {UserData} An OMEGA object.
 * @private
 */
function _createUserData(user: User, isSet: boolean): UserData {
  const internal: string = user.data && 'internal' in user.data ? user.data.internal.toString() : ANONYMOUS;
  const ud: UserData = {
    accountType: isSet ? user.accountType || ANONYMOUS : ANONYMOUS,
    authSystem: isSet ? user.authSystem || ANONYMOUS : ANONYMOUS,
    corpId: isSet ? user.groupId || ANONYMOUS : ANONYMOUS,
    corpName: isSet ? user.groupName || ANONYMOUS : ANONYMOUS,
    id: isSet ? user.userId || ANONYMOUS : ANONYMOUS,
    internal: isSet ? internal : ANONYMOUS,
    language: isSet ? user.language || ANONYMOUS : ANONYMOUS,
  };
  if (isSet) {
    return Object.assign({}, ud, {
      attributes: user.attributes,
      authOrigin: user.authOrigin,
      privileges: user.privileges,
    });
  } else {
    return ud;
  }
}

function applyUser(adobeMetrics: AdobeMetricsRuntime, m: Metric): void {
  if (!m.user && adobeMetrics.user) {
    m.user = {
      authId: adobeMetrics.user.authId,
      groupId: adobeMetrics.user.groupId,
      sessionId: adobeMetrics.user.sessionId,
      userId: adobeMetrics.user.userId,
    };
  }
}

/**
 * Special processing for Location record types. Moves
 * to gateway.
 *
 * @param {Metric} metric The metric being processed.
 */
function logLocation(metric: Metric): void {
  metric.event = 'Location.' + metric.event;
}

function logEvent(m: Metric): void {
  // not all events will have data, but if the user and metricsStates objects are in data,
  // they are promoted to the metrics object
  if (m.data && m.data[0]) {
    if (m.data[0].user) {
      m.user = m.data[0].user;
    }
    if (m.data[0].metricsState) {
      m.metricsState = m.data[0].metricsState;
    }
  }
}

export default (
  adobeMetrics: AdobeMetricsRuntime,
  global: Window & typeof globalThis,
  metrics: MetricsApi,
): MetricsProcessing => {
  const {clearInterval, setInterval} = global;
  /**
   * We track HTTP connections in the window in MetricsNetworkIntercept. To reduce contention after
   * pageDone, where we reduced the flush interval to 3s, we increase flushPeriod as a function of
   * total connection count if the connection count is ≥ 1 & current interval is < 15s.
   */
  function checkConnections(): void {
    const newFlushPeriod = Math.min(3000 + adobeMetrics.connections * 1000, INIT_FLUSH_PERIOD);
    Object.assign(adobeMetrics.runtime.options.transport, {
      flushPeriod: newFlushPeriod,
    });
  }
  /**
   * Determine if a metric is an event that should trigger a flush to remote storage.
   *
   * @param {Metric} metric The metric properties to be checked.
   * @returns {string | undefined} A string reason if queued metrics should be flushed.
   */
  function isFlushEventFn(metric: Metric): string | undefined {
    // Location records always flush
    if (RecordType.LOCATION === metric.recordType) {
      return 'location';
    }

    // Event records flush if PAGE_LOAD_DONE
    if (metric.event) {
      const events = Array.isArray(metric.event) ? metric.event : [metric.event];
      const done = events.some((e: string): boolean => MetricsEvents.PAGE_LOAD_DONE === e);
      if (done) {
        if (adobeMetrics.transport.transmit) {
          Object.assign(adobeMetrics.runtime.options.transport, {
            flushPeriod: 3000,
          });
          cleanMetricsInterval('checkConnectionsInterval');
          adobeMetrics.init.checkConnectionsInterval = setInterval(checkConnections, 2000);
        }
        return 'pageDone';
      }
    }
    return undefined;
  }
  let isFlushEvent = isFlushEventFn;
  /**
   * Compare user with current Metrics and user properties. Only userId, groupId and
   * sessionId are used to perform the compare. Other properties are considered
   * state properties, but differences in those properties do not designate a
   * different user.
   *
   * @param {User} user The user properties to check against the
   * current Metrics.user.
   * @returns {boolean} True if the provided user is the current metrics user.
   */
  function isCurrentUser(user: User): boolean {
    const {user: previousUser} = adobeMetrics;
    if (user && previousUser) {
      return (
        user.userId === previousUser.userId &&
        user.groupId === previousUser.groupId &&
        user.sessionId === previousUser.sessionId &&
        previousUser.event === user.event
      );
    }
    return false;
  }
  /**
   * Convenience method to apply user opt-out selection in the Metric.
   *
   * @param {MetricsState} ms The MS bag owned by sdk.
   * @private
   */
  function _setOptOut(ms: MetricsState): void {
    const {document} = global;
    if (document?.cookie && document.cookie.search(re) > -1) {
      ms.flags = Object.assign({}, ms.flags, {user: {optOut: true}});
      adobeMetrics.metricsState = Object.assign({}, adobeMetrics.metricsState, ms);
    }
  }
  /**
   * Convenience method to set timestamp, event, and recordType properties to the User object.
   *
   * @param {User} user The user properties to transmogrify.
   * @param {boolean} isSet "true" if called by setUser, "false" if called by clearUser.
   * @returns {User} A modified User object.
   * @private
   */
  function _userMetadata(user: User, isSet: boolean): User {
    if (isSet) {
      user = Object.assign({}, adobeMetrics.user, user);
    }

    return Object.assign({}, user, {
      dateNow: Date.now(),
      event: isSet ? user.event : undefined,
      recordType: 'User',
    });
  }

  /**
   * Make sure the metricsState is non-null and has the current value of
   * adobeMetrics pageState.
   *
   * @param {MetricsState} state The metricsState to be annotated.
   * @returns {MetricsState} An updated state, or a new state if the state param was null.
   */
  function applyPageState(state?: MetricsState): MetricsState {
    return Object.assign({}, state, {
      pageState: Object.assign({}, adobeMetrics.pageState),
    });
  }

  function _sendUserMetric(um: User, ms: MetricsState, isSet: boolean): void {
    const updated = isSet ? um.data || um.event : undefined;
    if (adobeMetrics.transport.transmit && (updated || !isCurrentUser(um))) {
      if (isSet) {
        _setOptOut(ms);
      }
      adobeMetrics.payload.addUser(Object.assign({}, um), ms);
      // when _createMetricNoPii() is supported by the server, we can just
      // send the metric: {event: 'exc.metrics.setUser', recordType: 'Event', user: user}
      // then DecorateUtility will scrub the PII from the Log metric.
      adobeMetrics.payload.addMetric(isSet ? _createSetUserMetric(um) : _clearMetric(), applyPageState(ms));
    }
  }

  /**
   * Special processing for Analytics record types.
   *
   * @param {Metric} metric The metric being processed.
   */
  function logAnalytics(metric: Metric): void {
    // return quickly for things we don't handle
    if (!metric.event || Array.isArray(metric.event) || metric.event === 'Launch.post') {
      return;
    }

    const {_satellite, document, location} = global;
    const omegaEvents = ['event', 'page', 'user'];
    const telemetryEvents = ['Event', 'Page', 'User'];
    const prefix = 'Analytics.';
    const d: DigitalData = (global.digitalData = (global.digitalData || {
      event: [],
      page: {solution: {}},
      user: {},
    }) as DigitalData);
    let e: EventData | PageData | UserData;
    const dataIsArray = Array.isArray(metric.data);

    // "omegaEvents" come from Metrics.analytics.track(). It is incumbent on the application
    // to execute _satellite.track() explicitly.
    if (omegaEvents.includes(metric.event)) {
      if (dataIsArray) {
        metric.data = [d, ...metric.data];
      } else if (metric.data) {
        metric.data = [d, metric.data];
      } else {
        metric.data = d;
      }
      metric.event = prefix + metric.event;
    } else if (telemetryEvents.includes(metric.event)) {
      // It's a documented EIM feature to forward "telemetry Event" to OMEGA via _satellite.track()
      // These events correspond to metrics.analytics.trackEvent, trackPage, trackUser calls

      // metric event contains a EventData, PageData or UserData
      // put it into window.DigitalData
      e = dataIsArray ? metric.data[0] : metric.data;

      switch (metric.event) {
        case 'Event':
        default:
          d.event = e ? [e as EventData] : [];
          break;
        case 'Page':
          d.page = d.page || ({solution: {}} as PageData);
          d.page = Object.assign({}, d.page, e);
          d.page.url = location?.href;
          d.page.name = d.page.name || document.title;
          d.page.hierarchy = d.page.hierarchy || d.page.name;
          break;
        case 'User':
          d.user = Object.assign({}, d.user, e);
          break;
      }

      _satellite?.track && _satellite.track(metric.event.toLowerCase());
      if (dataIsArray) {
        metric.data[0] = Object.assign({}, d);
      } else {
        metric.data = Object.assign({}, d);
      }
      metric.event = prefix + 'track' + metric.event;
    }
  }

  function logUserEvent(m: Metric): void {
    delete adobeMetrics.user?.event;
    let um = Object.assign({}, adobeMetrics.user, m.user);

    // if event or data are set on the incoming user object, force an update
    um = _userMetadata(um, true);
    _sendUserMetric(um, Object.assign({}, m.metricsState), true);
    adobeMetrics.user = um;

    // Only track user when it's from top window - unified shell
    // Because no solutions/other team use setUser or configure
    // This will avoid data overwrite within iframe to window.digitalData object
    const ms = adobeMetrics.metricsState;
    const iframe = ms?.window?.iframe;
    if (typeof iframe !== 'undefined' && !iframe) {
      // It will send the current user to both Analytics and telemetry
      metrics.analytics.trackUser(_createUserData(um, true));
    }
  }

  function _sendLogMetric(m: Metric): string | undefined {
    let flushReason: string | undefined;

    if (adobeMetrics.transport.transmit) {
      // for now, event arrays are processed client-side. Move to gateway.
      const originalEvent = m.event;
      _setOptOut(m.metricsState as MetricsState);
      if (Array.isArray(originalEvent) && originalEvent.length > 0) {
        originalEvent.forEach((e) => {
          m.event = e;
          adobeMetrics.payload.addMetric(
            m,
            Object.assign({}, m.metricsState, {
              guid: adobeMetrics.guid,
              sdkVersion: (m.metricsState && m.metricsState.sdkVersion) || METRICS_VERSION,
            }),
          );
        });
      } else {
        adobeMetrics.payload.addMetric(
          m,
          Object.assign({}, m.metricsState, {
            guid: adobeMetrics.guid,
            sdkVersion: (m.metricsState && m.metricsState.sdkVersion) || METRICS_VERSION,
          }),
        );
      }
      m.event = originalEvent;
      // Payloads with no user activity cause the Gateway to create user records for hash joins.
      // Those records don't have information like displayName. Ensuring that least one User
      // record is added to the payload ensures that meta information lands in ADX.
      if (
        adobeMetrics.user &&
        adobeMetrics.payload.countUser() === 0 &&
        adobeMetrics.user.userId &&
        adobeMetrics.user.groupId &&
        adobeMetrics.user.sessionId
      ) {
        adobeMetrics.payload.addUser(
          Object.assign({}, adobeMetrics.user, {dateNow: Date.now()}),
          Object.assign({}, m.metricsState),
        );
      }
      flushReason = isFlushEvent(m);
    }

    return flushReason;
  }

  function resetUserEvent(ms: MetricsState): void {
    const user = _userMetadata({}, false);

    _sendUserMetric(user, ms, false);

    adobeMetrics.user = user;

    // Clear user in analytics using anonymous values for required fields
    // It will send the anonymous user to OMEGA and an Analytics metric to telemetry
    metrics.analytics.trackUser(_createUserData({}, false));
  }

  function logApplicationEvent(m: Metric): void {
    if (!m.data || m.data.length === 0) {
      return;
    }

    const a = adobeMetrics.metricsState;
    const app = m.data[0]; // we don't expect more than one array element

    if (app) {
      if (app.id && a.application && a.application.id === app.id) {
        Object.assign(a.application, app);
      } else if (app && !app.id && app.solution) {
        a.application = Object.assign({}, a.application, app);
      } else {
        a.application = Object.assign({}, app);
      }
    } else {
      delete a.application;
    }
  }
  /**
   * Record a metric object to remote storage.
   *
   * @param {Metric} m The metric to be recorded.
   * @returns {string | undefined} A boolean indicating whether processed
   * metric should be flushed.
   */
  function processMetric(m: Metric): string | undefined {
    // must have a level for local console statement
    m.level = m.level || Level.INFO;

    applyUser(adobeMetrics, m);

    adobeMetrics.pageState.handleChange(m);

    switch (m.recordType) {
      case RecordType.ANALYTICS:
        logAnalytics(m);
        break;
      case RecordType.HISTORY:
        m.event = 'History.' + m.event;
        break;
      case RecordType.LOCATION:
        logLocation(m);
        break;
      case RecordType.EVENT:
        logEvent(m);

        // event came by way of setUser
        if (m.event === 'exc.metrics.setUser') {
          // logUserEvent adds this metric to the Payload, prepares the
          // Metric to be sent to OMEGA, then re-emits to this function as
          // RecordType.ANALYTICS.  Therefore, we can exit this function
          logUserEvent(m);
          return isFlushEvent(m);
        } else if (m.event === 'exc.metrics.clearUser') {
          // Reset the User object
          resetUserEvent(Object.assign({}, m.metricsState));
        } else if (m.event === 'exc.metrics.setApplication') {
          logApplicationEvent(m);
        }
        break;
      case RecordType.RECENT:
        if (m.data && m.data[0] && adobeMetrics.user) {
          if (!m.data[0].userId) {
            m.data[0].userId = adobeMetrics.user.userId;
          }
          if (!m.data[0].groupId) {
            m.data[0].groupId = adobeMetrics.user.groupId;
          }
        }
        break;
      default:
        break;
    }

    const key = m.level.toLowerCase();
    const {runtime} = adobeMetrics;
    if ((runtime.options.console as any)[key]) {
      (console as any)[key](
        `${m.name || ''}:${m.event || ''}: ${m.message || ''}`,
        runtime.options.console.metric ? m : m.data || '',
      );
    }

    m.metricsState = applyPageState(m.metricsState);
    return _sendLogMetric(m);
  }

  async function processQueue(flush: string | undefined = undefined): Promise<number> {
    const now = Date.now();
    let m;
    let count = 0;
    while ((m = adobeMetrics.queue.shift()) !== undefined) {
      m.dateNow = m.dateNow || now;
      const reason = processMetric(m);
      flush = flush || reason;
      ++count;
    }

    if (adobeMetrics.transport.transmit) {
      return adobeMetrics.transport.send(flush);
    }
    return Promise.resolve(count);
  }

  const cleanMetricsInterval = (intervalName: string): void => {
    const {init} = adobeMetrics;
    if (init[intervalName]) {
      clearInterval(init[intervalName]);
      delete init[intervalName];
    }
  };

  global.addEventListener &&
    global.addEventListener('pagehide', () => {
      cleanMetricsInterval('processQueueInterval');
      cleanMetricsInterval('checkConnectionsInterval');
    });

  function updateFlushEvent(config: Configuration): void {
    const {init} = adobeMetrics;
    cleanMetricsInterval('processQueueInterval');
    if (config.batch) {
      isFlushEvent = isFlushEventFn;
      init.processQueueInterval = setInterval(processQueue, 1000);
    } else {
      isFlushEvent = (): string => {
        return 'immediate';
      };
    }
  }
  function time(this: Timer, event?: string | {event: string}, ...args: any): number {
    this.dateNow = Date.now(); //record timestamp first
    (this as any).now = adobeMetrics.now();

    // clear old state
    delete this.data;
    delete this.event;
    delete this.message;
    this.level = Level.INFO;

    // calculate new event
    if (event && typeof event === 'object') {
      this.event = event.event;
    } else {
      this.event = (this.prefix || '') + (this.prefix && event ? '.' : '') + (event || '');
    }
    this.data = [...args];
    this.epoch = this.epoch || this.now;
    this.duration = this.now - this.epoch;
    adobeMetrics.write(Object.assign({}, this));
    return this.duration;
  }
  /**
   * Construct a property bag metric of a specific type using the properties
   * supplied, if any. The returned object can be Object assign()'d to the
   * constructed metric to allow for function definitions in a subclass.
   * The Metric level will default to INFO. To specify a different level, add
   * a level object to the args parameters in the form
   * {level: Level.DEBUG}.
   *
   * @param {RecordType} recordType The enum for which metric type to create.
   * @param {string} name The name gives context of what is being measured.
   * @param {any} args Optional objects with properties to be applied to the
   * recorded metrics.
   * @returns {Metric} An object conforming to the Metric interface that
   * implements Timer.
   */
  function create(recordType: RecordType, name?: string, ...args: any): Metric {
    let metric;
    const context = args[0];
    const prefix = args[1];
    const props = args.slice(2);

    const dateNow = Date.now();
    const epoch = adobeMetrics.now();
    switch (recordType) {
      case RecordType.TIMER:
      default:
        metric = Object.assign(
          {},
          {
            dateNow,
            epoch,
            level: Level.INFO,
            metricsState: {
              context,
              correlationId: uuidv1(),
              sdkVersion: METRICS_VERSION,
            },
            name,
            now: epoch,
            prefix,
            recordType: RecordType.TIMER,
            time,
          },
        );
        metric.time.bind(metric);
        metric.time('start', ...props);
        break;
    }
    return metric;
  }
  /**
   * Log a message to remote storage, with an optional data payload. In
   * developer mode, the log message will also be output to the console.
   * This method emulates console.log().
   *
   * @param {RecordType} recordType The type of metric being logged, defaults
   * to LOG.
   * @param {string} level The log level, which can be overridden by a
   * level object in the args list in the form {level: Level.DEBUG}.
   * @param {string} name The logger name.
   * @param {string} message The log message.
   * @param {string} event An event string or array of strings, typically
   * these are application-specific enums.
   * @param {any} args The rest of the objects to be logged. Primitives will
   *  be appended as strings to the message, and objects will be stored in an
   *  array in the data bag on the metric.
   */
  function log(
    recordType: RecordType,
    level: Level,
    name?: string,
    message?: string,
    event?: string | string[],
    ...args: any
  ): void {
    const m: Metric = {
      data: [...args],
      event,
      level,
      message,
      name,
      recordType,
    };
    adobeMetrics.write(m);
  }

  return {
    applyPageState,
    create,
    log,
    processQueue,
    updateFlushEvent,
  };
};
